import type { AppContext } from 'types/common'

import component from './Results.svelte'
import { graphql, type OrderByInstruction } from '$houdini'
import type { CustomerBalanceStatus } from 'utility/customer-helper'
import { stringToBoolean } from '@isoftdata/utility-string'

const customerSearchResultColumnPropertyMap = {
	id: 'id',
	contactName: 'contactName',
	companyName: 'companyName',
	'store[code]': 'storeCode',
	email: 'email',
	webAddress: 'webAddress',
	dateEntered: 'dateEntered',
	balance: 'balance',
	'defaultPaymentMethod[name]': 'defaultPaymentMethodName',
	'defaultTerm[name]': 'defaultTermName',
	'salesPerson[name]': 'salesPersonName',
	type: 'type',
	'billingAddress[address1]': 'street',
	'billingAddress[city]': 'city',
	'billingAddress[state]': 'state',
	'billingAddress[zip]': 'zip',
	'billingAddress[country]': 'country',
} as const

type CustomerSearchResultColumnProperty = keyof typeof customerSearchResultColumnPropertyMap
type CustomerSearchResultColumnPropertyValue =
	(typeof customerSearchResultColumnPropertyMap)[CustomerSearchResultColumnProperty]

function isAllowedCustomerSearchResultColumn(value: string): value is CustomerSearchResultColumnProperty {
	return value in customerSearchResultColumnPropertyMap
}

export default function ({ stateRouter }: AppContext) {
	stateRouter.addState({
		name: 'app.customer-search.results',
		route: 'results',
		querystringParameters: [
			'pageNumber',
			'perPageCount',
			'orderByColumn',
			'orderByDirection',
			'keyword',
			'lastSavedTime',
			'showActiveCustomersOnly',
		],
		defaultParameters: {
			pageNumber: '1',
			perPageCount: '10',
			showActiveCustomersOnly: 'true',
		},
		template: {
			svelte: true,
			component,
		},
		async resolve(_data, parameters) {
			const pageNumber = parseInt(parameters.pageNumber, 10) || 1
			const pageSize = parseInt(parameters.perPageCount, 10) || 10
			const showActiveCustomersOnly = stringToBoolean(parameters.showActiveCustomersOnly)
			const formattedOrderByColumn: CustomerSearchResultColumnPropertyValue | null =
				isAllowedCustomerSearchResultColumn(parameters.orderByColumn)
					? customerSearchResultColumnPropertyMap[parameters.orderByColumn]
					: null
			const orderBy: OrderByInstruction | undefined = formattedOrderByColumn
				? {
						field: formattedOrderByColumn,
						direction: parameters.orderByDirection === 'DESC' ? 'DESC' : 'ASC',
					}
				: undefined
			const { data } = await customerSearchQuery.fetch({
				variables: {
					filter: {
						keyword: parameters.keyword,
						active: showActiveCustomersOnly || null,
					},
					pagination: {
						pageNumber,
						pageSize,
					},
					orderBy: orderBy ? [orderBy] : undefined,
				},
			})

			if (!data) {
				throw new Error('Failed to load customers')
			}

			const customers = data.customers.items.map(customer => {
				let customerBalanceStatus: CustomerBalanceStatus
				const customerBalance = Number(customer.balance)
				const customerAccountLimit = Number(customer.accountLimit)

				if (customerBalance > 0 && customerBalance <= customerAccountLimit) {
					customerBalanceStatus = 'OWE_AND_UNDER_LIMIT'
				} else if (customerBalance > 0 && customerBalance >= customerAccountLimit) {
					customerBalanceStatus = 'OWE_AND_OVER_LIMIT'
				} else if (customerBalance === 0) {
					customerBalanceStatus = 'ZERO_BALANCE'
				} else if (customerBalance < 0) {
					customerBalanceStatus = 'CREDIT_BALANCE'
				} else if (!customer.active) {
					customerBalanceStatus = 'INACTIVE'
				} else {
					customerBalanceStatus = 'NO_STATUS'
				}

				return {
					...customer,
					balanceStatus: customerBalanceStatus,
				}
			})

			return {
				customers,
				showActiveCustomersOnly,
				perPageCount: data.customers.pageInfo.pageSize.toString(),
				currentPageNumber: data.customers.pageInfo.pageNumber,
				totalItemsCount: data.customers.totalItems,
				orderByColumn: parameters.orderByColumn,
				orderByDirection: parameters.orderByDirection,
			}
		},
	})
	stateRouter.on('stateChangeStart', state => {
		if (!state.name.startsWith('app.customer-search')) {
			sessionStorage.removeItem('selectedCustomers')
		}
	})
}

const customerSearchQuery = graphql(`
	query CustomerSearch($orderBy: [OrderByInstruction!], $pagination: PaginationOptions, $filter: CustomerFilter) {
		customers(orderBy: $orderBy, pagination: $pagination, filter: $filter) {
			items {
				id
				active
				contactName
				balance
				accountLimit
				dateEntered
				phoneNumber
				companyName
				store {
					code
				}
				faxNumber
				mobilePhoneNumber
				type
				billingAddress {
					address1
					city
					state
					zip
					country
				}
				email
				webAddress
				defaultTerm {
					name
				}
				salesPerson {
					name
				}
				defaultPaymentMethod {
					name
				}
				optionValues {
					option {
						id
						label
						dataType
						showInCustomerList
					}
					value
				}
			}
			pageInfo {
				pageNumber
				pageSize
				totalPages
			}
			totalItems
		}
	}
`)
